import React, { useState } from 'react';
import { X, CreditCard, Smartphone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_AMOUNTS = [500, 1000, 2000, 5000, 10000];

/**
 * Reusable mobile-money top-up modal.
 * Used for both Family credits (endpoint: /api/family/{id}/credits/top-up) and
 * Personal wallet (endpoint: /api/credits/{id}/top-up).
 *
 * Props:
 *  - title: header label (e.g. "Top up Family Credit")
 *  - currentBalance: number (currently displayed under the header)
 *  - endpoint: full URL to POST to (already includes user/family id)
 *  - defaultProvider: 'airtel' | 'tnm' | 'mpamba'
 *  - phoneSuggestion: pre-fill for the "from" phone field
 *  - onSuccess(data): called with backend response on 200
 *  - onClose(): called to dismiss the modal
 */
const TopUpCreditsModal = ({ title = 'Top up Credit', currentBalance = 0, endpoint, defaultProvider = 'airtel', phoneSuggestion = '', onSuccess, onClose }) => {
  const [amount, setAmount] = useState('');
  const [provider, setProvider] = useState(defaultProvider);
  const [phone, setPhone] = useState(phoneSuggestion);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      alert('Enter an amount greater than 0');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt, provider, phone: phone || null })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data?.detail || 'Top-up failed');
        return;
      }
      if (onSuccess) onSuccess(data);
    } catch (_) {
      alert('Network error during top-up');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="topup-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-green-600" />
              {title}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Current balance: K{(currentBalance || 0).toLocaleString()}</p>
          </div>
          <button onClick={onClose} data-testid="topup-close-btn" className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Amount */}
          <div>
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Amount (MWK)</label>
            <input
              type="number"
              min={0}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="e.g. 2000"
              data-testid="topup-amount-input"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-base font-semibold dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {QUICK_AMOUNTS.map(a => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAmount(String(a))}
                  data-testid={`topup-quick-${a}`}
                  className="px-2.5 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                >
                  K{a.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          {/* Provider */}
          <div>
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Mobile Money Provider</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'airtel', label: 'Airtel Money', accent: 'red' },
                { value: 'tnm', label: 'TNM Mpamba', accent: 'green' },
              ].map(p => {
                const active = provider === p.value;
                const accentBg = p.accent === 'red' ? 'bg-red-50 border-red-500 text-red-700' : 'bg-green-50 border-green-500 text-green-700';
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setProvider(p.value)}
                    data-testid={`topup-provider-${p.value}`}
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-semibold transition-colors ${
                      active ? accentBg : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    <Smartphone className="w-3.5 h-3.5" />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Charge from phone (optional)</label>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder={phoneSuggestion || 'e.g. 0991234567'}
              data-testid="topup-phone-input"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white"
            />
          </div>

          <p className="text-[10px] text-gray-500 italic">
            Mobile money charge is currently simulated. Real Airtel Money / TNM Mpamba integration coming soon — your top-up is recorded immediately.
          </p>
        </div>

        <div className="px-5 py-4 border-t dark:border-gray-700 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={submitting} className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !amount || parseFloat(amount) <= 0}
            data-testid="topup-confirm-btn"
            className="px-5 py-2 text-sm rounded-lg bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white font-semibold"
          >
            {submitting ? 'Processing…' : `Top up K${(parseFloat(amount) || 0).toLocaleString()}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopUpCreditsModal;
